import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { HotspotLike } from './FigmaEventProxyService';
import { FigmaEventProxyService } from './FigmaEventProxyService';

interface FigmaPresentedNodeChangedEvent {
    type: 'PRESENTED_NODE_CHANGED';
    data: {
        presentedNodeId: string;
        interactionType?: string;
        isStoredInHistory?: boolean;
        stateMappings?: Record<string, string>;
    };
}

interface FigmaMousePressOrReleaseEvent {
    type: 'MOUSE_PRESS_OR_RELEASE';
    data: {
        pressed: boolean;
        point?: { x: number; y: number };
        targetNodeId?: string;
        targetNodeMousePosition?: { x: number; y: number };
        // Id ближайшего к месту клика scrolling frame. Для кликов внутри вложенных
        // скроллящихся фреймов (карусели и т.п.) это НЕ экран, а сам вложенный
        // фрейм — и координаты ниже относительны ему, а не экрану.
        nearestScrollingFrameId?: string;
        // Координаты относительно ближайшего scrolling frame (в дизайн-пикселях).
        // ВАЖНО: это координаты ВИДИМОГО вьюпорта фрейма — внутренняя прокрутка
        // в них НЕ учтена (проверено на живых событиях: при offset.y=1758
        // mousePosition.y остаётся в пределах высоты вьюпорта). Чтобы получить
        // координаты контента, нужно добавить nearestScrollingFrameOffset.
        // Если scrolling frame — вложенный фрейм, нужно ещё добавить его позицию
        // на экране (см. nestedFrames).
        nearestScrollingFrameMousePosition?: { x: number; y: number };
        // Смещение прокрутки scrolling frame. Добавляем к mousePosition,
        // чтобы получить координаты контента (в них заданы hotspot'ы).
        nearestScrollingFrameOffset?: { x: number; y: number };
        presentedNodeId?: string;
    };
}

interface FigmaInitialLoadEvent {
    type: 'INITIAL_LOAD';
    data: {
        presentedNodeId: string;
    };
}

interface FigmaNewStateEvent {
    type: 'NEW_STATE';
    data: {
        stateMappings: Record<string, string>;
        currentPageId?: string;
        currentTopLevelFrameId?: string;
    };
}

type FigmaEmbedEvent =
    | FigmaPresentedNodeChangedEvent
    | FigmaMousePressOrReleaseEvent
    | FigmaInitialLoadEvent
    | FigmaNewStateEvent;

export interface FigmaNestedFrame {
    id: string;
    // Позиция вложенного скролл-фрейма относительно экрана (дизайн-пиксели).
    // Плагин экспортирует их в screen.nestedFrames (x/y — относительно корневого фрейма).
    x: number;
    y: number;
    width: number;
    height: number;
}

interface FigmaEmbedViewerProps<H extends HotspotLike = HotspotLike> {
    fileId: string;
    nodeId: string;
    fileName?: string;
    hotspots: Array<H>;
    currentScreen: string | null;
    // Вложенные скролл-фреймы текущего экрана (карусели и т.п.).
    // Нужны, чтобы переводить координаты клика из системы вложенного фрейма
    // (в них Figma присылает nearestScrollingFrameMousePosition) в систему экрана.
    nestedFrames?: Array<FigmaNestedFrame>;
    width?: number;
    height?: number;
    scale?: number;
    originalWidth?: number;
    originalHeight?: number;
    // Натуральные размеры экспортированного изображения экрана (screen.imageUrl).
    // Для экранов с внутренним скроллом изображение содержит весь контент
    // и больше viewport'а фрейма (originalWidth/Height).
    screenImageWidth?: number;
    screenImageHeight?: number;
    // Масштаб, с которым бэкенд экспортирует изображения экранов (обычно 2 = @2x).
    exportScale?: number;
    // Размеры контента на экспортированной картинке в дизайн-пикселях
    // (приходят от плагина в screen.contentWidth/Height). Имеют приоритет
    // над связкой screenImage/exportScale.
    contentWidth?: number;
    contentHeight?: number;
    onHotspotClick?: (hotspot: H, clickX: number, clickY: number, currentScreenId: string | null) => void;
    onEmptyAreaClick?: (clickX: number, clickY: number, currentScreenId: string | null) => void;
    onScreenChange?: (figmaNodeId: string) => void;
    onLoad?: () => void;
    debugOverlayEnabled?: boolean;
    hideUI?: boolean;
    hotspotHints?: boolean;
    scaling?: 'scale-down' | 'contain' | 'min-zoom' | 'scale-down-width' | 'fit-width' | 'free';
    contentScaling?: 'fixed' | 'responsive';
    maxWidth?: string;
    maxHeight?: string;
    bgColor?: string;
    figmaClientId?: string;
    embedHost?: string;
}

export function FigmaEmbedViewer<H extends HotspotLike = HotspotLike>({
    fileId,
    nodeId,
    fileName = 'fileName',
    hotspots,
    currentScreen,
    nestedFrames,
    width,
    height,
    scale = 1,
    originalWidth,
    originalHeight,
    screenImageWidth,
    screenImageHeight,
    exportScale = 2,
    contentWidth,
    contentHeight,
    onHotspotClick,
    onEmptyAreaClick,
    onScreenChange,
    onLoad,
    debugOverlayEnabled = false,
    hideUI = true,
    hotspotHints = false,
    scaling = 'scale-down-width',
    contentScaling = 'fixed',
    maxWidth = '100%',
    maxHeight,
    bgColor = '000000',
    figmaClientId,
    embedHost = 'figma-analytics',
}: FigmaEmbedViewerProps<H>) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Загрузка завершается по INITIAL_LOAD из Embed Kit 2.0 (прототип реально
    // отрендерен) либо по load iframe + таймауту как фолбэку без client-id.
    const loadFiredRef = useRef(false);
    const loadTimeoutRef = useRef<number | null>(null);
    const fallbackTimeoutRef = useRef<number | null>(null);
    const onLoadRef = useRef(onLoad);
    onLoadRef.current = onLoad;

    const eventProxyServiceRef = useRef<FigmaEventProxyService<H> | null>(null);

    const clearLoadTimers = useCallback(() => {
        if (loadTimeoutRef.current) {
            clearTimeout(loadTimeoutRef.current);
            loadTimeoutRef.current = null;
        }
        if (fallbackTimeoutRef.current) {
            clearTimeout(fallbackTimeoutRef.current);
            fallbackTimeoutRef.current = null;
        }
    }, []);

    // Завершает загрузку ровно один раз: снимает лоадер и вызывает onLoad.
    const finishLoading = useCallback(() => {
        if (loadFiredRef.current) return;
        loadFiredRef.current = true;
        clearLoadTimers();
        setIsLoading(false);
        onLoadRef.current?.();
    }, [clearLoadTimers]);

    // Инициализируем сервис один раз при монтировании.
    useEffect(() => {
        eventProxyServiceRef.current = new FigmaEventProxyService<H>({
            onHotspotClick: (hotspot, clickX, clickY, currentScreenId) => {
                onHotspotClick?.(hotspot, clickX, clickY, currentScreenId);
            },
            debug: debugOverlayEnabled,
        });

        return () => {
            eventProxyServiceRef.current?.cleanup();
            eventProxyServiceRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Обновляем сервис при смене экрана, hotspot или масштаба.
    useEffect(() => {
        eventProxyServiceRef.current?.updateScreenAndHotspots(currentScreen, hotspots);
        eventProxyServiceRef.current?.setScale(scale);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentScreen, hotspots, scale]);

    // Формируем URL для Figma embed.
    const getIframeUrl = (currentNodeId: string) => {
        const params = new URLSearchParams();

        params.append('embed-host', embedHost);

        if (figmaClientId) {
            params.append('client-id', figmaClientId);
        }

        if (currentNodeId) {
            params.append('node-id', currentNodeId);
            params.append('starting-point-node-id', currentNodeId);
        }

        params.append('hide-ui', hideUI ? '1' : '0');
        params.append('footer', '0');
        params.append('viewport-controls', '0');
        params.append('hotspot-hints', hotspotHints ? '1' : '0');
        params.append('device-frame', '0');
        params.append('disable-default-keyboard-nav', '1');
        params.append('scaling', scaling);
        params.append('content-scaling', contentScaling);

        return `https://embed.figma.com/proto/${fileId}?${params.toString()}`;
    };

    const iframeUrl = getIframeUrl(nodeId);

    // Слушаем postMessage от Figma. Без client-id события не приходят,
    // но код оставлен на случай, если клиентский id будет настроен позже.
    useEffect(() => {
        const hasEmbedKit2 = !!figmaClientId;

        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data;

            const figmaOrigins = ['https://www.figma.com', 'https://embed.figma.com'];
            const isFigmaOrigin = figmaOrigins.some((origin) => event.origin.startsWith(origin));

            if (!isFigmaOrigin) {
                return;
            }

            const eventData = rawData as FigmaEmbedEvent | { type?: string; data?: unknown };
            if (!eventData || typeof eventData !== 'object') return;

            const eventType = eventData.type;
            const data = 'data' in eventData ? eventData.data : eventData;

            if (eventType === 'INITIAL_LOAD') {
                const initialData = data as FigmaInitialLoadEvent['data'];
                if (initialData?.presentedNodeId) {
                    onScreenChange?.(initialData.presentedNodeId);
                }
                // INITIAL_LOAD из Embed Kit 2.0 приходит, когда первый фрейм
                // прототипа реально отрендерен, — в отличие от load iframe,
                // который срабатывает на загрузку оболочки. Завершаем загрузку
                // (и старт таймера вопроса) по этому событию.
                finishLoading();
                return;
            }

            if (eventType === 'PRESENTED_NODE_CHANGED') {
                const nodeChangedData = data as FigmaPresentedNodeChangedEvent['data'];
                const presentedNodeId = nodeChangedData?.presentedNodeId;
                if (presentedNodeId) {
                    onScreenChange?.(presentedNodeId);
                }
                return;
            }

            if (eventType === 'MOUSE_PRESS_OR_RELEASE' && hasEmbedKit2) {
                const mouseData = data as FigmaMousePressOrReleaseEvent['data'];
                // Figma может присылать событие как с pressed=true (нажатие), так и
                // без этого поля. Обрабатываем любое, кроме явного pressed=false.
                if (mouseData?.pressed !== false) {
                    if (debugOverlayEnabled) {
                        // Debug: сырые данные события от Figma. Важно для разбора
                        // кликов внутри фреймов с собственным скроллом.
                        // eslint-disable-next-line no-console
                        // JSON.stringify — чтобы в CDP-логах видеть вложенные объекты целиком
                        // (preview в console.debug обрезает вложенность).
                        console.debug('[FigmaEmbedViewer] MOUSE_PRESS_OR_RELEASE raw ' + JSON.stringify(mouseData));
                    }
                    const rawPointX = mouseData.point?.x;
                    const rawPointY = mouseData.point?.y;
                    const rawTargetX = mouseData.targetNodeMousePosition?.x;
                    const rawTargetY = mouseData.targetNodeMousePosition?.y;
                    const rawFrameX = mouseData.nearestScrollingFrameMousePosition?.x;
                    const rawFrameY = mouseData.nearestScrollingFrameMousePosition?.y;
                    const scrollingFrameId = mouseData.nearestScrollingFrameId;

                    // Предпочитаем координаты относительно текущего scrolling frame.
                    // Fallback'и на случай, если Figma не пришлёт это поле.
                    const hasFrameCoords = rawFrameX !== undefined && rawFrameY !== undefined;
                    const visibleClickX = rawFrameX ?? rawPointX ?? rawTargetX ?? 0;
                    const visibleClickY = rawFrameY ?? rawPointY ?? rawTargetY ?? 0;

                    // Переводим координаты в систему контента экрана:
                    // 1) mousePosition — относительно видимого вьюпорта scrolling frame,
                    //    поэтому добавляем offset прокрутки этого фрейма;
                    // 2) если scrolling frame — вложенный (карусель и т.п.), добавляем
                    //    ещё позицию этого фрейма на экране (screen.nestedFrames).
                    const scrollOffsetX = mouseData.nearestScrollingFrameOffset?.x ?? 0;
                    const scrollOffsetY = mouseData.nearestScrollingFrameOffset?.y ?? 0;
                    const nestedFrame =
                        scrollingFrameId && nestedFrames
                            ? nestedFrames.find((f) => f.id === scrollingFrameId)
                            : undefined;
                    const frameClickX = visibleClickX + scrollOffsetX + (nestedFrame?.x ?? 0);
                    const frameClickY = visibleClickY + scrollOffsetY + (nestedFrame?.y ?? 0);

                    // Для поиска hotspot координаты должны быть в тех же пикселях,
                    // в которых заданы hotspot (оригинальные пиксели экрана).
                    // nearestScrollingFrameMousePosition обычно уже в них.
                    // Для point/targetNodeMousePosition пробуем привести через масштаб iframe.
                    const iframeWidth = typeof width === 'number' && width > 0 ? width : originalWidth || 1;
                    const iframeHeight = typeof height === 'number' && height > 0 ? height : originalHeight || 1;
                    const needsScaling = !hasFrameCoords;
                    const originalClickX =
                        needsScaling && originalWidth && originalWidth > 0
                            ? (frameClickX * originalWidth) / iframeWidth
                            : frameClickX;
                    const originalClickY =
                        needsScaling && originalHeight && originalHeight > 0
                            ? (frameClickY * originalHeight) / iframeHeight
                            : frameClickY;

                    // Храним координаты в процентах от размеров экспортированного
                    // изображения экрана (0–100): в результатах клик рисуется поверх
                    // этого изображения. База процента — размер контента картинки в
                    // дизайн-пикселях: приоритет у contentWidth/Height от плагина,
                    // для старых прототипов выводим из натуральных размеров картинки
                    // и exportScale; в последнюю очередь — viewport (originalWidth/Height).
                    const percentBaseX =
                        contentWidth && contentWidth > 0
                            ? contentWidth
                            : screenImageWidth && exportScale > 0
                            ? screenImageWidth / exportScale
                            : originalWidth || 0;
                    const percentBaseY =
                        contentHeight && contentHeight > 0
                            ? contentHeight
                            : screenImageHeight && exportScale > 0
                            ? screenImageHeight / exportScale
                            : originalHeight || 0;
                    const percentX = percentBaseX > 0 ? (originalClickX / percentBaseX) * 100 : originalClickX;
                    const percentY = percentBaseY > 0 ? (originalClickY / percentBaseY) * 100 : originalClickY;

                    const screenIdForClick = currentScreen || eventProxyServiceRef.current?.getCurrentScreen() || null;
                    const clickedHotspot = eventProxyServiceRef.current?.findHotspotAt(originalClickX, originalClickY);

                    if (debugOverlayEnabled) {
                        // Debug: промежуточные и итоговые координаты клика,
                        // чтобы сопоставить их с hotspot и понять, где ломается нормализация
                        // (особенно при кликах во вложенные скролл-фреймы — см. nestedFrames).
                        // eslint-disable-next-line no-console
                        console.debug(
                            '[FigmaEmbedViewer] click computed ' +
                                JSON.stringify({
                                    visibleClickX,
                                    visibleClickY,
                                    scrollingFrameId,
                                    scrollOffsetX,
                                    scrollOffsetY,
                                    nestedFrame: nestedFrame ?? null,
                                    frameClickX,
                                    frameClickY,
                                    needsScaling,
                                    iframeWidth,
                                    iframeHeight,
                                    originalWidth,
                                    originalHeight,
                                    originalClickX,
                                    originalClickY,
                                    screenImageWidth,
                                    screenImageHeight,
                                    exportScale,
                                    contentWidth,
                                    contentHeight,
                                    percentBaseX,
                                    percentBaseY,
                                    percentX,
                                    percentY,
                                    currentScreen: screenIdForClick,
                                    matchedHotspot: clickedHotspot ?? null,
                                }),
                        );
                    }

                    if (clickedHotspot) {
                        onHotspotClick?.(clickedHotspot, percentX, percentY, screenIdForClick);
                    } else {
                        onEmptyAreaClick?.(percentX, percentY, screenIdForClick);
                    }
                }
                return;
            }

            if (eventType === 'NEW_STATE') {
                // Событие истории навигации — можно расширить при необходимости.
                return;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [
        figmaClientId,
        embedHost,
        currentScreen,
        nestedFrames,
        scale,
        onHotspotClick,
        onEmptyAreaClick,
        onScreenChange,
        debugOverlayEnabled,
        finishLoading,
    ]);

    // Обработка событий загрузки iframe.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        loadFiredRef.current = false;

        const handleLoad = () => {
            // С Embed Kit 2.0 (есть client-id) load iframe срабатывает на загрузку
            // оболочки — сам прототип ещё не отрендерен. Загрузку завершаем
            // по событию INITIAL_LOAD из postMessage (см. выше).
            if (figmaClientId) return;

            if (fallbackTimeoutRef.current) {
                clearTimeout(fallbackTimeoutRef.current);
                fallbackTimeoutRef.current = null;
            }
            loadTimeoutRef.current = window.setTimeout(finishLoading, 2000);
        };

        const handleError = () => {
            setLoadError('Ошибка загрузки прототипа Figma');
            setIsLoading(false);
            clearLoadTimers();
        };

        iframe.addEventListener('load', handleLoad);
        iframe.addEventListener('error', handleError);

        // Страховка на случай, если INITIAL_LOAD не придёт
        // (Embed Kit без client-id, сбой внутри iframe и т.п.).
        fallbackTimeoutRef.current = window.setTimeout(finishLoading, figmaClientId ? 15000 : 5000);

        return () => {
            iframe.removeEventListener('load', handleLoad);
            iframe.removeEventListener('error', handleError);
            clearLoadTimers();
        };
    }, [iframeUrl, figmaClientId, finishLoading, clearLoadTimers]);

    // Перехватываем клики на контейнере в фазе capture.
    const handleContainerMouseDownCapture = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!eventProxyServiceRef.current || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const rawClickX = e.clientX - rect.left;
        const rawClickY = e.clientY - rect.top;
        const clickX = rawClickX / scale;
        const clickY = rawClickY / scale;
        const screenIdForClick = currentScreen || eventProxyServiceRef.current.getCurrentScreen() || null;

        const clickedHotspot = eventProxyServiceRef.current.findHotspotAt(clickX, clickY);

        if (debugOverlayEnabled) {
            // Debug: координаты клика по контейнеру (capture-фаза).
            // Эти координаты считаются без учёта внутреннего скролла фрейма Figma,
            // поэтому для фреймов со скроллом могут расходиться с реальным положением клика.
            // eslint-disable-next-line no-console
            console.debug('[FigmaEmbedViewer] container mousedown (capture)', {
                rawClickX,
                rawClickY,
                scale,
                clickX,
                clickY,
                currentScreen: screenIdForClick,
                matchedHotspot: clickedHotspot ?? null,
            });
        }

        if (clickedHotspot) {
            onHotspotClick?.(clickedHotspot, clickX, clickY, screenIdForClick);
        } else {
            onEmptyAreaClick?.(clickX, clickY, screenIdForClick);
        }

        // НЕ вызываем preventDefault / stopPropagation — клик должен дойти до iframe,
        // чтобы Figma сама обработала навигацию.
    };

    const containerWidth = typeof width === 'number' ? `${width}px` : width || '100%';
    const containerHeight = typeof height === 'number' ? `${height}px` : height || '600px';

    return (
        <div
            ref={containerRef}
            className='figma-embed-viewer'
            style={{
                position: 'relative',
                width: containerWidth,
                height: containerHeight,
                overflow: 'hidden',
                margin: '0 auto',
                maxWidth,
                maxHeight,
                backgroundColor: bgColor ? `#${bgColor}` : '#000000',
            }}
            onMouseDownCapture={handleContainerMouseDownCapture}
        >
            {loadError && <div className='figma-embed-viewer__error'>{loadError}</div>}

            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    backgroundColor: bgColor ? `#${bgColor}` : '#000000',
                }}
            >
                <iframe
                    ref={iframeRef}
                    src={iframeUrl}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 0,
                        display: 'block',
                        pointerEvents: 'auto',
                    }}
                    allowFullScreen
                    title={`Figma Prototype ${fileName}`}
                    sandbox='allow-same-origin allow-scripts allow-popups allow-forms'
                />

                {!isLoading && !loadError && eventProxyServiceRef.current && (
                    <div key={`hotspot-overlays-${currentScreen || 'none'}`}>
                        {eventProxyServiceRef.current.renderHotspotOverlays()}
                    </div>
                )}
            </div>
        </div>
    );
}
